/**
 * 離屏侵蝕引擎 — 在隱藏的 WebGL2 Canvas 上運行 bufferA/B/C/D
 *
 * 架構要點：
 * - 不進行任何可見渲染（沒有 image pass）
 * - bufferA: 接收筆刷輸入，寫入高度圖
 * - bufferB: 從高度圖運行 Phacelle Noise 侵蝕
 * - bufferC: 噪聲紋理（只渲染一次）
 * - 透過 terrainBuffer 共享數據（零分配 readPixels）
 */

import { terrainBuffer } from './terrainBuffer'

// ─── Types ──────────────────────────────────────────────────

export interface BrushInput {
  active: boolean
  position: [number, number] // UV 0-1
  tool: number
  radius: number
  strength: number
  target: number
  shift: boolean
}

interface FBO {
  texA: WebGLTexture
  texB: WebGLTexture
  fboA: WebGLFramebuffer
  fboB: WebGLFramebuffer
  width: number
  height: number
  pingpong: number
}

interface Uniforms {
  iResolution: WebGLUniformLocation | null
  iTime: WebGLUniformLocation | null
  iTimeDelta: WebGLUniformLocation | null
  iFrame: WebGLUniformLocation | null
  iFrameRate: WebGLUniformLocation | null
  iMouse: WebGLUniformLocation | null
  iDate: WebGLUniformLocation | null
  iChannel0: WebGLUniformLocation | null
  iChannel1: WebGLUniformLocation | null
  iChannel2: WebGLUniformLocation | null
  iChannel3: WebGLUniformLocation | null
  iChannelResolution: WebGLUniformLocation | null
  uCamera: WebGLUniformLocation | null
  uBrushTool: WebGLUniformLocation | null
  uBrushRadius: WebGLUniformLocation | null
  uBrushStrength: WebGLUniformLocation | null
  uBrushTarget: WebGLUniformLocation | null
  uBrushPos: WebGLUniformLocation | null
  uBrushActive: WebGLUniformLocation | null
  uShiftDown: WebGLUniformLocation | null
}

// ─── Shader 載入與編譯 ──────────────────────────────────────

/**
 * 目前應用所在目錄（結尾必有 /）。
 * 若頁面為 /shanhaijing（無尾隨斜線），相對路徑 ./shaders 會被瀏覽器解析成 /shaders，導致 404 回 HTML，
 * Shader 編譯即出現 `'<': syntax error`。
 */
function getAppBasePath(): string {
  if (typeof window === 'undefined') return '/'
  const { pathname } = window.location
  if (pathname.endsWith('/')) return pathname
  if (pathname.endsWith('.html')) {
    const i = pathname.lastIndexOf('/')
    return i >= 0 ? pathname.slice(0, i + 1) : '/'
  }
  return `${pathname}/`
}

function resolveShaderUrl(filename: string): string {
  const viteBase = import.meta.env.BASE_URL
  // 子專案為 base: '/shanhaimap/' 等絕對路徑時，shader 必須掛在該 base 下（與 pathname 是否帶尾階斜線無關）
  if (viteBase.startsWith('/')) {
    const base = viteBase.endsWith('/') ? viteBase : `${viteBase}/`
    return `${base}shaders/${filename}`.replace(/\/{2,}/g, '/')
  }
  const basePath = getAppBasePath()
  return `${basePath}shaders/${filename}`.replace(/\/{2,}/g, '/')
}

async function loadShaderSource(url: string): Promise<string> {
  const res = await fetch(url)
  if (!res.ok) throw new Error(`Failed to load ${url}: ${res.status}`)
  const text = await res.text()
  const head = text.trimStart().slice(0, 64).toLowerCase()
  if (
    head.startsWith('<!doctype') ||
    head.startsWith('<html') ||
    head.startsWith('<head') ||
    head.startsWith('<body')
  ) {
    throw new Error(
      `Shader 請求回傳了 HTML 而非 GLSL（多為路徑錯誤或 404）：${url}`,
    )
  }
  return text
}

const VERTEX_SHADER_SRC = `#version 300 es
layout(location=0) in vec2 aPos;
void main() { gl_Position = vec4(aPos, 0.0, 1.0); }
`

function wrapFragmentShader(commonSrc: string, mainSrc: string): string {
  return `#version 300 es
precision highp float;
precision highp int;

uniform vec3 iResolution;
uniform float iTime;
uniform float iTimeDelta;
uniform int iFrame;
uniform float iFrameRate;
uniform vec4 iMouse;
uniform vec4 iDate;
uniform vec3 iChannelResolution[4];
uniform sampler2D iChannel0;
uniform sampler2D iChannel1;
uniform sampler2D iChannel2;
uniform sampler2D iChannel3;

// 攝影機（侵蝕引擎不需要視覺攝影機，但 common.glsl 宣告了）
uniform vec3 uCamera;

out vec4 fragColorOut;

// ---- Common ----
${commonSrc}

// ---- Main ----
${mainSrc}

void main() {
  vec4 col = vec4(0.0);
  mainImage(col, gl_FragCoord.xy);
  fragColorOut = col;
}
`
}

function compileShader(gl: WebGL2RenderingContext, type: number, src: string): WebGLShader | null {
  const s = gl.createShader(type)!
  gl.shaderSource(s, src)
  gl.compileShader(s)
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    console.error('Shader compile error:', gl.getShaderInfoLog(s))
    gl.deleteShader(s)
    return null
  }
  return s
}

function createProgram(gl: WebGL2RenderingContext, fragSrc: string): WebGLProgram | null {
  const vs = compileShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER_SRC)
  const fs = compileShader(gl, gl.FRAGMENT_SHADER, fragSrc)
  if (!vs || !fs) return null
  const p = gl.createProgram()!
  gl.attachShader(p, vs)
  gl.attachShader(p, fs)
  gl.linkProgram(p)
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
    console.error('Program link error:', gl.getProgramInfoLog(p))
    return null
  }
  return p
}

function createTexture(gl: WebGL2RenderingContext, w: number, h: number): WebGLTexture {
  const tex = gl.createTexture()!
  gl.bindTexture(gl.TEXTURE_2D, tex)
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA32F, w, h, 0, gl.RGBA, gl.FLOAT, null)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
  return tex
}

function createFBO(gl: WebGL2RenderingContext, w: number, h: number): FBO {
  const texA = createTexture(gl, w, h)
  const texB = createTexture(gl, w, h)
  const fboA = gl.createFramebuffer()!
  const fboB = gl.createFramebuffer()!
  gl.bindFramebuffer(gl.FRAMEBUFFER, fboA)
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texA, 0)
  gl.bindFramebuffer(gl.FRAMEBUFFER, fboB)
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texB, 0)
  gl.bindFramebuffer(gl.FRAMEBUFFER, null)
  return { texA, texB, fboA, fboB, width: w, height: h, pingpong: 0 }
}

function getReadTex(fbo: FBO): WebGLTexture {
  return fbo.pingpong === 0 ? fbo.texA : fbo.texB
}

function getUniforms(gl: WebGL2RenderingContext, prog: WebGLProgram): Uniforms {
  return {
    iResolution: gl.getUniformLocation(prog, 'iResolution'),
    iTime: gl.getUniformLocation(prog, 'iTime'),
    iTimeDelta: gl.getUniformLocation(prog, 'iTimeDelta'),
    iFrame: gl.getUniformLocation(prog, 'iFrame'),
    iFrameRate: gl.getUniformLocation(prog, 'iFrameRate'),
    iMouse: gl.getUniformLocation(prog, 'iMouse'),
    iDate: gl.getUniformLocation(prog, 'iDate'),
    iChannel0: gl.getUniformLocation(prog, 'iChannel0'),
    iChannel1: gl.getUniformLocation(prog, 'iChannel1'),
    iChannel2: gl.getUniformLocation(prog, 'iChannel2'),
    iChannel3: gl.getUniformLocation(prog, 'iChannel3'),
    iChannelResolution: gl.getUniformLocation(prog, 'iChannelResolution'),
    uCamera: gl.getUniformLocation(prog, 'uCamera'),
    uBrushTool: gl.getUniformLocation(prog, 'uBrushTool'),
    uBrushRadius: gl.getUniformLocation(prog, 'uBrushRadius'),
    uBrushStrength: gl.getUniformLocation(prog, 'uBrushStrength'),
    uBrushTarget: gl.getUniformLocation(prog, 'uBrushTarget'),
    uBrushPos: gl.getUniformLocation(prog, 'uBrushPos'),
    uBrushActive: gl.getUniformLocation(prog, 'uBrushActive'),
    uShiftDown: gl.getUniformLocation(prog, 'uShiftDown'),
  }
}

// ─── 引擎主體 ───────────────────────────────────────────────

const READBACK_INTERVAL = 4  // 筆刷活躍時 N 幀讀回一次
const IDLE_READBACK_INTERVAL = 30  // 侵蝕冷卻期降低讀回頻率
const EROSION_COOLDOWN = 180  // 停筆後繼續侵蝕的幀數（~3 秒 @60fps）

export class ErosionEngine {
  private canvas: HTMLCanvasElement
  private gl: WebGL2RenderingContext
  private size: number
  private disposed = false
  private animId = 0
  private ready = false

  // FBO
  private fboA!: FBO
  private fboB!: FBO
  private fboC!: FBO

  // Programs & uniforms
  private pA!: WebGLProgram
  private pB!: WebGLProgram
  private pC!: WebGLProgram
  private uA!: Uniforms
  private uB!: Uniforms
  private uC!: Uniforms

  // 全螢幕三角形 VAO + VBO
  private quadVAO!: WebGLVertexArrayObject
  private quadVBO!: WebGLBuffer

  // 鍵盤紋理（bufferB 用 Enter 切換侵蝕）
  private kbTex!: WebGLTexture
  private kbData = new Uint8Array(256 * 3)

  // 預分配（避免每幀 new）
  private chRes = new Float32Array(12)
  private cachedDate = new Date()
  private dateUpdateCounter = 0

  // 狀態
  private frameCount = 0
  private startTime = 0
  private lastTime = 0
  private brush: BrushInput = {
    active: false,
    position: [0.5, 0.5],
    tool: 0,
    radius: 0.2,
    strength: 0.5,
    target: 0.45,
    shift: false,
  }
  private erosionCountdown = 0  // 侵蝕冷卻倒數
  private bufferCReady = false
  private framesUntilReadback = 0

  // Ready / Error 回調（只觸發一次）
  private _readyCallback: (() => void) | null = null
  private _errorCallback: ((msg: string) => void) | null = null

  constructor(size: number = 512) {
    this.size = size

    // 建立離屏 canvas（不加入 DOM）
    this.canvas = document.createElement('canvas')
    this.canvas.width = size
    this.canvas.height = size

    const gl = this.canvas.getContext('webgl2', {
      antialias: false,
      preserveDrawingBuffer: false,
    })
    if (!gl) throw new Error('WebGL2 not available for ErosionEngine')
    this.gl = gl

    gl.getExtension('EXT_color_buffer_float')
    gl.getExtension('OES_texture_float_linear')

    // 初始化 VAO
    this.quadVAO = gl.createVertexArray()!
    gl.bindVertexArray(this.quadVAO)
    this.quadVBO = gl.createBuffer()!
    gl.bindBuffer(gl.ARRAY_BUFFER, this.quadVBO)
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW)
    gl.enableVertexAttribArray(0)
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0)
    gl.bindVertexArray(null)

    // 鍵盤紋理
    this.kbTex = gl.createTexture()!
    gl.bindTexture(gl.TEXTURE_2D, this.kbTex)
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.R8, 256, 3, 0, gl.RED, gl.UNSIGNED_BYTE, this.kbData)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)

    // FBO
    this.fboA = createFBO(gl, size, size)
    this.fboB = createFBO(gl, size, size)
    this.fboC = createFBO(gl, size, size)
  }

  /** 註冊 ready 回調（引擎首次讀回有效數據時觸發，僅一次） */
  onReady(cb: () => void) {
    this._readyCallback = cb
  }

  /** 註冊錯誤回調（shader 載入/編譯失敗時觸發） */
  onError(cb: (msg: string) => void) {
    this._errorCallback = cb
  }

  /** 載入 shader 並啟動運行迴圈 */
  async start() {
    const gl = this.gl
    const S = this.size

    let commonSrc: string, bufferASrc: string, bufferBSrc: string, bufferCSrc: string
    try {
      ;[commonSrc, bufferASrc, bufferBSrc, bufferCSrc] = await Promise.all([
        loadShaderSource(resolveShaderUrl('common.glsl')),
        loadShaderSource(resolveShaderUrl('bufferA.glsl')),
        loadShaderSource(resolveShaderUrl('bufferB.glsl')),
        loadShaderSource(resolveShaderUrl('bufferC.glsl')),
      ])
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Shader 載入失敗'
      console.error('ErosionEngine:', msg)
      this._errorCallback?.(msg)
      this._errorCallback = null
      return
    }

    if (this.disposed) return

    const pA = createProgram(gl, wrapFragmentShader(commonSrc, bufferASrc))
    const pB = createProgram(gl, wrapFragmentShader(commonSrc, bufferBSrc))
    const pC = createProgram(gl, wrapFragmentShader(commonSrc, bufferCSrc))

    if (!pA || !pB || !pC) {
      const msg = 'Shader 編譯失敗，無法啟動地形引擎'
      console.error('ErosionEngine:', msg)
      this._errorCallback?.(msg)
      this._errorCallback = null
      return
    }

    this.pA = pA; this.pB = pB; this.pC = pC
    this.uA = getUniforms(gl, pA)
    this.uB = getUniforms(gl, pB)
    this.uC = getUniforms(gl, pC)

    this.startTime = performance.now() / 1000
    this.lastTime = this.startTime
    this.ready = true

    const loop = () => {
      if (this.disposed) return
      this.tick()
      this.animId = requestAnimationFrame(loop)
    }
    this.animId = requestAnimationFrame(loop)
  }

  /** 更新筆刷狀態 */
  setBrush(input: Partial<BrushInput>) {
    Object.assign(this.brush, input)
  }

  /** 把存檔高度圖寫回 GPU 與 terrainBuffer，之後筆刷仍可接著改 */
  restoreHeightmap(data: Float32Array) {
    const gl = this.gl
    const S = this.size
    if (!this.ready || data.length !== S * S * 4) return
    terrainBuffer.heightmap.set(data)
    terrainBuffer.heightmapVersion++
    terrainBuffer.getHeightVersion++
    terrainBuffer.ready = true
    for (const fbo of [this.fboA, this.fboB]) {
      for (const tex of [fbo.texA, fbo.texB]) {
        gl.bindTexture(gl.TEXTURE_2D, tex)
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA32F, S, S, 0, gl.RGBA, gl.FLOAT, data)
      }
    }
    gl.bindTexture(gl.TEXTURE_2D, null)
  }

  /** 讀回高度圖到 terrainBuffer（零分配） */
  private readbackHeightmap() {
    const gl = this.gl
    const fbo = this.fboB
    const readFBO = fbo.pingpong === 0 ? fbo.fboA : fbo.fboB
    gl.bindFramebuffer(gl.FRAMEBUFFER, readFBO)
    gl.readPixels(0, 0, this.size, this.size, gl.RGBA, gl.FLOAT, terrainBuffer.heightmap)
    gl.bindFramebuffer(gl.FRAMEBUFFER, null)
    terrainBuffer.heightmapVersion++
    terrainBuffer.getHeightVersion++
    if (!terrainBuffer.ready) {
      terrainBuffer.ready = true
      this._readyCallback?.()
      this._readyCallback = null
    }
  }

  // ─── 內部方法 ────────────────────────────────────────────

  private setCommonUniforms(u: Uniforms, time: number, timeDelta: number, channelRes: Float32Array | null) {
    const gl = this.gl
    const S = this.size
    gl.uniform3f(u.iResolution!, S, S, 1.0)
    gl.uniform1f(u.iTime!, time)
    gl.uniform1f(u.iTimeDelta!, timeDelta)
    gl.uniform1i(u.iFrame!, this.frameCount)
    gl.uniform1f(u.iFrameRate!, timeDelta > 0 ? 1.0 / timeDelta : 60.0)
    // iMouse 不再用於筆刷定位，但 shader 中仍有宣告，設為 0
    gl.uniform4f(u.iMouse!, 0, 0, 0, 0)
    if (this.dateUpdateCounter++ % 60 === 0) {
      this.cachedDate.setTime(Date.now())
    }
    const d = this.cachedDate
    gl.uniform4f(u.iDate!, d.getFullYear(), d.getMonth(), d.getDate(),
      d.getHours() * 3600 + d.getMinutes() * 60 + d.getSeconds() + d.getMilliseconds() / 1000)
    if (channelRes && u.iChannelResolution) {
      gl.uniform3fv(u.iChannelResolution, channelRes)
    }
    // 攝影機 uniform（侵蝕引擎不需要但 common.glsl 有宣告）
    if (u.uCamera) gl.uniform3f(u.uCamera, 0, -0.43, 3.25)

    // 筆刷 uniform
    if (u.uBrushTool !== null) gl.uniform1i(u.uBrushTool, this.brush.tool)
    if (u.uBrushRadius !== null) gl.uniform1f(u.uBrushRadius, this.brush.radius)
    if (u.uBrushStrength !== null) gl.uniform1f(u.uBrushStrength, this.brush.strength)
    if (u.uBrushTarget !== null) gl.uniform1f(u.uBrushTarget, this.brush.target)
    if (u.uBrushPos !== null) gl.uniform2f(u.uBrushPos, this.brush.position[0], this.brush.position[1])
    if (u.uBrushActive !== null) gl.uniform1i(u.uBrushActive, this.brush.active ? 1 : 0)
    if (u.uShiftDown !== null) gl.uniform1i(u.uShiftDown, this.brush.shift ? 1 : 0)
  }

  private bindTex(unit: number, tex: WebGLTexture) {
    this.gl.activeTexture(this.gl.TEXTURE0 + unit)
    this.gl.bindTexture(this.gl.TEXTURE_2D, tex)
  }

  private renderPass(
    prog: WebGLProgram, u: Uniforms, fbo: FBO,
    channels: (WebGLTexture | null)[], channelRes: Float32Array,
    time: number, timeDelta: number,
  ) {
    const gl = this.gl
    gl.useProgram(prog)
    this.setCommonUniforms(u, time, timeDelta, channelRes)
    for (let i = 0; i < 4; i++) {
      gl.uniform1i(u[`iChannel${i}` as keyof Uniforms] as WebGLUniformLocation, i)
      if (channels[i]) this.bindTex(i, channels[i]!)
    }
    const writeFBO = fbo.pingpong === 0 ? fbo.fboB : fbo.fboA
    gl.bindFramebuffer(gl.FRAMEBUFFER, writeFBO)
    gl.viewport(0, 0, fbo.width, fbo.height)
    gl.bindVertexArray(this.quadVAO)
    gl.drawArrays(gl.TRIANGLES, 0, 3)
    fbo.pingpong = 1 - fbo.pingpong
  }

  private tick() {
    if (!this.ready) return
    const S = this.size
    const now = performance.now() / 1000
    const time = now - this.startTime
    const timeDelta = now - this.lastTime

    const isTerrainBrush = this.brush.active

    // 重用預分配的 channel resolution 陣列
    const chRes = this.chRes
    chRes[0] = S; chRes[1] = S; chRes[2] = 1
    chRes[3] = 256; chRes[4] = 3; chRes[5] = 1
    chRes[6] = S; chRes[7] = S; chRes[8] = 1

    // 侵蝕倒數：筆刷活躍時重置，停筆後繼續侵蝕一段時間再停止
    if (isTerrainBrush) {
      this.erosionCountdown = EROSION_COOLDOWN
    }
    const needErosion = isTerrainBrush || this.erosionCountdown > 0

    // bufferA: 地形筆刷 — 僅筆刷活躍或初始化時運行
    if (isTerrainBrush || this.frameCount < 4) {
      this.renderPass(this.pA, this.uA, this.fboA,
        [getReadTex(this.fboA), this.kbTex, null, null], chRes, time, timeDelta)
    }

    // bufferB: 侵蝕 — 筆刷活躍 + 冷卻期間運行，穩定後停止
    if (needErosion || this.frameCount < 4) {
      this.renderPass(this.pB, this.uB, this.fboB,
        [getReadTex(this.fboA), this.kbTex, null, null], chRes, time, timeDelta)
      if (!isTerrainBrush && this.erosionCountdown > 0) {
        this.erosionCountdown--
      }
    }

    // bufferC: 噪聲紋理（只渲染一次）
    if (!this.bufferCReady) {
      this.renderPass(this.pC, this.uC, this.fboC,
        [null, null, null, null], new Float32Array(12), time, timeDelta)
      this.bufferCReady = true
    }

    // 定期讀回到 terrainBuffer
    const anyActive = isTerrainBrush || needErosion || this.frameCount < 4
    if (anyActive) {
      this.framesUntilReadback--
      if (this.framesUntilReadback <= 0) {
        const readInterval = isTerrainBrush ? READBACK_INTERVAL : IDLE_READBACK_INTERVAL
        this.framesUntilReadback = readInterval
        this.readbackHeightmap()
      }
    }

    this.frameCount++
    this.lastTime = now
  }

  private deleteFBO(fbo: FBO) {
    const gl = this.gl
    gl.deleteTexture(fbo.texA)
    gl.deleteTexture(fbo.texB)
    gl.deleteFramebuffer(fbo.fboA)
    gl.deleteFramebuffer(fbo.fboB)
  }

  /** 銷毀引擎，釋放所有 WebGL 資源 */
  dispose() {
    this.disposed = true
    cancelAnimationFrame(this.animId)
    this._readyCallback = null
    this._errorCallback = null

    const gl = this.gl

    if (this.pA) gl.deleteProgram(this.pA)
    if (this.pB) gl.deleteProgram(this.pB)
    if (this.pC) gl.deleteProgram(this.pC)

    if (this.fboA) this.deleteFBO(this.fboA)
    if (this.fboB) this.deleteFBO(this.fboB)
    if (this.fboC) this.deleteFBO(this.fboC)

    if (this.quadVBO) gl.deleteBuffer(this.quadVBO)
    if (this.quadVAO) gl.deleteVertexArray(this.quadVAO)
    if (this.kbTex) gl.deleteTexture(this.kbTex)
  }
}
